import React, { useState } from 'react';
import { useTranslations } from 'next-intl';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';

interface DeleteAccountConfirmationProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  userEmail: string;
  isLoading: boolean;
}

const DeleteAccountConfirmation: React.FC<DeleteAccountConfirmationProps> = ({
  isOpen,
  onClose,
  onConfirm,
  userEmail,
  isLoading,
}) => {
  const [emailInput, setEmailInput] = useState('');
  const [emailError, setEmailError] = useState('');
  const t = useTranslations('Confirmation.DeleteAccount');

  // 重置状态当弹窗关闭时
  React.useEffect(() => {
    if (!isOpen) {
      setEmailInput('');
      setEmailError('');
    }
  }, [isOpen]);

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setEmailInput(value);

    // 清除错误提示当用户开始输入时
    if (emailError) {
      setEmailError('');
    }
  };

  const handleConfirm = () => {
    // 验证邮箱是否匹配
    if (emailInput.trim().toLowerCase() !== userEmail.toLowerCase()) {
      setEmailError(t('emailError'));
      return;
    }

    onConfirm();
  };

  const handleCancel = () => {
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className='w-11/12 max-w-md rounded-lg p-0'>
        {/* Header */}
        <div className='flex items-center justify-center border-gray-200 px-6 pt-4'>
          <DialogTitle className='text-xl font-semibold'>
            {t('title')}
          </DialogTitle>
        </div>

        {/* Content */}
        <div className='px-6 py-4'>
          <p className='mb-6 text-center text-gray-700'>{t('message')}</p>

          <div className='space-y-4'>
            <div>
              <input
                type='email'
                placeholder={t('emailPlaceholder')}
                value={emailInput}
                onChange={handleEmailChange}
                className={`w-full rounded-lg border px-4 py-2 text-sm focus:outline-none focus:ring-2 ${
                  emailError
                    ? 'border-red-300 focus:ring-red-500'
                    : 'border-gray-300 focus:ring-blue-500'
                }`}
                disabled={isLoading}
                autoComplete='email'
              />
              {emailError && (
                <p className='mt-1 text-sm text-red-500'>{emailError}</p>
              )}
            </div>

            <div className='flex flex-col gap-3'>
              <button
                onClick={handleConfirm}
                disabled={isLoading}
                className={`w-full rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
                  isLoading
                    ? 'cursor-not-allowed bg-gray-200 text-gray-400'
                    : 'bg-red-500 text-white hover:bg-red-600'
                }`}
              >
                {isLoading ? t('processing') : t('confirm')}
              </button>

              <button
                onClick={handleCancel}
                disabled={isLoading}
                className='w-full rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50'
              >
                {t('cancel')}
              </button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default DeleteAccountConfirmation;
